import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from "swiper/react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faTimes,
  faUserMd,
  faGavel,
  faHandsHelping,
  faHandshake,
  faBuilding,
  faUsers,
  faMapMarkerAlt,
  faCheckCircle,
  faStar,
  faHeart,
  faGlobe,
  faBriefcase
} from '@fortawesome/free-solid-svg-icons';
import "swiper/css";
import { Autoplay } from "swiper/modules";
import axios from 'axios';
import { baseUrl } from '../../config';

const OrganizationScrolling = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const staggerItem = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 },
    transition: { duration: 0.5 }
  };

  const filterEntriesByRole = (role) => {
    const filtered = entries.filter(
      (entry) =>
        entry.role.toLowerCase().trim() === role.toLowerCase().trim() &&
        entry.status === "Approved"
    );
    return filtered;
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    const searchResults = entries.filter(
      (entry) =>
        entry.status === "Approved" &&
        (entry.name.toLowerCase().includes(query.toLowerCase()) ||
          entry._id.toLowerCase().includes(query.toLowerCase()))
    );

    setFilteredEntries(searchResults);
  };

  const categories = [
    {
      name: "Doctor",
      role: "doctor",
      icon: faUserMd,
      color: "from-blue-500 to-blue-600",
      bgColor: "from-blue-50 to-blue-100",
      description: "Verified medical professionals"
    },
    {
      name: "Advocate",
      role: "advocate",
      icon: faGavel,
      color: "from-purple-500 to-purple-600",
      bgColor: "from-purple-50 to-purple-100",
      description: "Legal experts and advocates"
    },
    {
      name: "NGO",
      role: "ngo",
      icon: faHandsHelping,
      color: "from-green-500 to-green-600",
      bgColor: "from-green-50 to-green-100",
      description: "Non-profit organizations"
    },
    {
      name: "Partner",
      role: "partner",
      icon: faHandshake,
      color: "from-orange-500 to-orange-600",
      bgColor: "from-orange-50 to-orange-100",
      description: "Strategic business partners"
    },
    {
      name: "Company",
      role: "company",
      icon: faBuilding,
      color: "from-indigo-500 to-indigo-600",
      bgColor: "from-indigo-50 to-indigo-100",
      description: "Corporate organizations"
    },
    {
      name: "Organization",
      role: "organization",
      icon: faUsers,
      color: "from-pink-500 to-pink-600",
      bgColor: "from-pink-50 to-pink-100",
      description: "Professional organizations"
    },
  ];

  // Fetch real advocates from API
  const fetchAdvocates = async () => {
    try {
      setLoading(true);
      const apiUrl = baseUrl || 'https://githubtool.xyz';
      const response = await axios.get(`${apiUrl}/advocate/approved`, {
        timeout: 10000
      });

      if (response.data.success && response.data.data) {
        // Transform advocate data to match the expected format
        const advocateEntries = response.data.data.map(advocate => ({
          _id: advocate._id,
          name: advocate.name,
          role: 'advocate',
          city: advocate.city,
          state: advocate.state,
          photoUrl: advocate.profileImage,
          status: 'Approved',
          specialization: advocate.specialization,
          experience: advocate.experience,
          rating: advocate.rating || 5
        }));

        // Mock data for other categories (keeping them as demo)
        const mockOtherEntries = [
          { _id: "doc001", name: "Dr. Rajesh Kumar", role: "doctor", city: "Delhi", state: "Delhi", photoUrl: "https://randomuser.me/api/portraits/men/1.jpg", status: "Approved" },
          { _id: "ngo001", name: "Green Earth NGO", role: "ngo", city: "Bangalore", state: "Karnataka", photoUrl: "https://randomuser.me/api/portraits/men/2.jpg", status: "Approved" },
          { _id: "comp001", name: "TechCorp Solutions", role: "company", city: "Hyderabad", state: "Telangana", photoUrl: "https://randomuser.me/api/portraits/women/2.jpg", status: "Approved" },
          { _id: "org001", name: "Media Professionals", role: "organization", city: "Chennai", state: "Tamil Nadu", photoUrl: "https://randomuser.me/api/portraits/men/3.jpg", status: "Approved" },
          { _id: "part001", name: "Digital Partners", role: "partner", city: "Pune", state: "Maharashtra", photoUrl: "https://randomuser.me/api/portraits/women/3.jpg", status: "Approved" },
        ];

        // Combine real advocates with mock data for other categories
        setEntries([...advocateEntries, ...mockOtherEntries]);
        console.log('✅ Loaded', advocateEntries.length, 'real advocates');
      } else {
        // If API fails, use only mock data for non-advocate categories
        const mockEntries = [
          { _id: "doc001", name: "Dr. Rajesh Kumar", role: "doctor", city: "Delhi", state: "Delhi", photoUrl: "https://randomuser.me/api/portraits/men/1.jpg", status: "Approved" },
          { _id: "ngo001", name: "Green Earth NGO", role: "ngo", city: "Bangalore", state: "Karnataka", photoUrl: "https://randomuser.me/api/portraits/men/2.jpg", status: "Approved" },
          { _id: "comp001", name: "TechCorp Solutions", role: "company", city: "Hyderabad", state: "Telangana", photoUrl: "https://randomuser.me/api/portraits/women/2.jpg", status: "Approved" },
          { _id: "org001", name: "Media Professionals", role: "organization", city: "Chennai", state: "Tamil Nadu", photoUrl: "https://randomuser.me/api/portraits/men/3.jpg", status: "Approved" },
          { _id: "part001", name: "Digital Partners", role: "partner", city: "Pune", state: "Maharashtra", photoUrl: "https://randomuser.me/api/portraits/women/3.jpg", status: "Approved" },
        ];
        setEntries(mockEntries);
      }
    } catch (error) {
      console.error('❌ Error fetching advocates:', error);
      // Fallback to mock data for non-advocate categories
      const mockEntries = [
        { _id: "doc001", name: "Dr. Rajesh Kumar", role: "doctor", city: "Delhi", state: "Delhi", photoUrl: "https://randomuser.me/api/portraits/men/1.jpg", status: "Approved" },
        { _id: "ngo001", name: "Green Earth NGO", role: "ngo", city: "Bangalore", state: "Karnataka", photoUrl: "https://randomuser.me/api/portraits/men/2.jpg", status: "Approved" },
        { _id: "comp001", name: "TechCorp Solutions", role: "company", city: "Hyderabad", state: "Telangana", photoUrl: "https://randomuser.me/api/portraits/women/2.jpg", status: "Approved" },
        { _id: "org001", name: "Media Professionals", role: "organization", city: "Chennai", state: "Tamil Nadu", photoUrl: "https://randomuser.me/api/portraits/men/3.jpg", status: "Approved" },
        { _id: "part001", name: "Digital Partners", role: "partner", city: "Pune", state: "Maharashtra", photoUrl: "https://randomuser.me/api/portraits/women/3.jpg", status: "Approved" },
      ];
      setEntries(mockEntries);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdvocates();
  }, []);

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-blue-50 pt-20 pb-8 px-2 sm:px-4">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.95 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-7xl mx-auto mb-16"
      >
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12">
          <div className="text-center">
            <div className="inline-block bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
              IINSAF ORGANIZATION
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              <span className="text-red-500">ii</span>nsaf <span className="bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">Network</span>
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-blue-600 mx-auto rounded-full mb-6"></div>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Connect with our network of verified professionals, organizations, and service providers across India
            </p>
          </div>
        </div>
      </motion.div>

      {/* Search Section */}
      <div className="w-full max-w-7xl mx-auto mb-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6 sm:p-8"
        >
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                <FontAwesomeIcon icon={faSearch} className="text-lg" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by name or organization ID..."
                className="w-full pl-12 pr-12 py-4 border-2 border-gray-100 rounded-2xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 bg-white shadow-sm hover:shadow-md text-lg"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setFilteredEntries([]);
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-lg" />
                </button>
              )}
            </div>

            {/* Search Stats */}
            {searchQuery && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 text-center"
              >
                <span className="text-sm text-gray-500 bg-gray-100 px-4 py-2 rounded-full">
                  {filteredEntries.length} result{filteredEntries.length !== 1 ? 's' : ''} found
                </span>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Categories Section */}
      <div className="w-full max-w-7xl mx-auto">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-16"
        >
          {categories.map((category, index) => {
            const filteredByRole = filterEntriesByRole(category.role);

            if (filteredByRole.length === 0) return null;

            return (
              <motion.div
                key={index}
                variants={staggerItem}
                className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 overflow-hidden"
              >
                {/* Category Header */}
                <div className={`bg-gradient-to-r ${category.color} p-6 sm:p-8`}>
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                      <FontAwesomeIcon icon={category.icon} className="text-white text-2xl" />
                    </div>
                    <div className="text-center">
                      <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                        {category.name}s
                      </h2>
                      <p className="text-white/90 text-lg">{category.description}</p>
                    </div>
                  </div>
                </div>

                {/* Swiper Container */}
                <div className="p-6 sm:p-8">
                  <Swiper
                    modules={[Autoplay]}
                    spaceBetween={20}
                    slidesPerView={6}
                    loop={true}
                    speed={5000}
                    autoplay={{
                      delay: 0,
                      disableOnInteraction: false,
                    }}
                    style={{ transitionTimingFunction: 'linear !important' }}
                    breakpoints={{
                      0: { slidesPerView: 1, spaceBetween: 10 },
                      640: { slidesPerView: 2, spaceBetween: 15 },
                      768: { slidesPerView: 3, spaceBetween: 15 },
                      1024: { slidesPerView: 4, spaceBetween: 20 },
                      1280: { slidesPerView: 6, spaceBetween: 20 },
                    }}
                  >
                    {filteredByRole.map((entry) => (
                      <SwiperSlide key={entry._id}>
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.5 }}
                          className={`group bg-white rounded-2xl shadow-lg border border-gray-100 p-6 ${category.role === 'advocate' ? 'h-[280px]' : 'h-[200px]'
                            } flex flex-col items-center justify-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2`}
                        >
                          {/* Profile Image */}
                          <div className="relative mb-4">
                            <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-lg group-hover:scale-110 transition-transform duration-300">
                              <img
                                src={entry.photoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(entry.name)}&size=200&background=9333ea&color=fff&bold=true`}
                                alt={entry.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(entry.name)}&size=200&background=9333ea&color=fff&bold=true`;
                                }}
                              />
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                              <FontAwesomeIcon icon={faCheckCircle} className="text-white text-xs" />
                            </div>
                          </div>

                          {/* Content */}
                          <div className="text-center w-full">
                            <h3 className="text-lg font-bold text-gray-800 mb-1 group-hover:text-blue-600 transition-colors line-clamp-2">
                              {entry.name}
                            </h3>
                            <div className="flex items-center justify-center gap-1 text-sm text-gray-600 mb-2">
                              <FontAwesomeIcon icon={faMapMarkerAlt} className="text-xs" />
                              <span className="line-clamp-1">{entry.city}, {entry.state}</span>
                            </div>

                            {/* Advocate-specific details */}
                            {category.role === 'advocate' && entry.specialization && (
                              <div className="mb-2">
                                <p className="text-xs text-purple-600 font-semibold bg-purple-50 rounded px-2 py-1 line-clamp-2">
                                  {entry.specialization}
                                </p>
                              </div>
                            )}

                            {/* Experience badge for advocates */}
                            {category.role === 'advocate' && entry.experience && (
                              <div className="flex items-center justify-center gap-1 mb-2">
                                <FontAwesomeIcon icon={faBriefcase} className="text-purple-500 text-xs" />
                                <span className="text-xs text-gray-600 font-medium">
                                  {entry.experience}+ years
                                </span>
                              </div>
                            )}

                            {/* Star rating */}
                            <div className="flex items-center justify-center gap-1">
                              {[...Array(5)].map((_, index) => (
                                <FontAwesomeIcon
                                  key={index}
                                  icon={faStar}
                                  className={`text-xs ${index < (entry.rating || 5) ? 'text-yellow-400' : 'text-gray-300'
                                    }`}
                                />
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      {/* Stats Section */}
      <div className="w-full max-w-7xl mx-auto mt-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              Our <span className="bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">Network</span> Impact
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Building connections across India's professional landscape
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { number: "1000+", label: "Verified Professionals", icon: faUsers, color: "from-blue-500 to-blue-600" },
              { number: "50+", label: "Cities Covered", icon: faMapMarkerAlt, color: "from-green-500 to-green-600" },
              { number: "25+", label: "States Active", icon: faGlobe, color: "from-purple-500 to-purple-600" },
              { number: "99%", label: "Satisfaction Rate", icon: faHeart, color: "from-red-500 to-red-600" }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${stat.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <FontAwesomeIcon icon={stat.icon} className="text-white text-xl" />
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">{stat.number}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OrganizationScrolling;